import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from 'shared-ui/lib/components/Card';
import { CardTitle } from 'shared-ui/lib/components/CardTitle';
import { GenericIndexScene } from 'shared-ui/lib/components/table/TableX';
import { setupQueryConfig } from 'shared-ui/lib/config/query.config';
import { vehicleColumns } from '../../columns/vehicle.columns';
import UserContext from '../../contexes/UserContext';
import { useVehicleIndexGroupsQuery, useVehicleIndexSceneLazyQuery } from '../../generated/graphql';
import { getGroupDisplayName } from '../../utils/get-group-display-name';

const selectClassName =
  'appearance-none block min-w-[12rem] px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm';

export const VehicleIndexScene = (): JSX.Element => {
  const { t } = useTranslation();
  const [user] = useContext(UserContext);
  const isBusiness = !!user.business;
  const [groupId, setGroupId] = useState('');
  const groupsQuery = useVehicleIndexGroupsQuery({
    ...setupQueryConfig(),
    skip: !isBusiness,
  });
  const query = useVehicleIndexSceneLazyQuery(setupQueryConfig());
  const groups = groupsQuery.data?.myGroups.list ?? [];
  const defaultGroupId = groups[0]?.id ?? '';
  const selectedGroupId = groupId || defaultGroupId;
  const where = selectedGroupId ? { groupIds: { has: selectedGroupId } } : undefined;

  const groupFilter =
    isBusiness && groups.length > 0 ? (
      <div className="mb-4">
        <label
          htmlFor="vehicle-group-filter"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {t('VehicleIndexScene:filterGroup')}
        </label>
        <select
          id="vehicle-group-filter"
          className={selectClassName}
          value={selectedGroupId}
          onChange={(event) => setGroupId(event.target.value)}
        >
          {groups.map((group) => (
            <option key={group.id} value={group.id}>
              {getGroupDisplayName(group, t)}
            </option>
          ))}
        </select>
      </div>
    ) : null;

  return (
    <Card>
      <>
        <CardTitle>{t('VehicleIndexScene:title')}</CardTitle>
        {groupFilter}
        <GenericIndexScene
          key={selectedGroupId}
          unwrapped
          dataPath="vehicles.list"
          countPath="vehicles.totalCount"
          query={query}
          where={where}
          columns={[
            vehicleColumns.name,
            vehicleColumns.registerNumber,
            vehicleColumns.VIN,
            vehicleColumns.license,
            vehicleColumns.model,
            vehicleColumns.brand,
            vehicleColumns.linkToVehicle,
          ]}
        />
      </>
    </Card>
  );
};
